import React, { useEffect, useMemo, useRef, useState } from 'react';
import UploadsPanel from '../toolbox/UploadsPanel';
import ElementsPanel from './elements/ElementsPanel';

const getLayerLabel = (element) => {
  if (!element) return 'Layer';
  if (element.type === 'text') return element.text || 'Text';
  if (element.type === 'image') return 'Image';
  if (element.type === 'frame') return 'Frame';
  if (element.type === 'shape') {
    const shapeLabel = element.shapeType || 'Shape';
    return shapeLabel.charAt(0).toUpperCase() + shapeLabel.slice(1);
  }
  return 'Layer';
};

const LayersPanel = ({
  elements,
  selectedIds,
  onSelect,
  activeTool,
  onToolChange,
  onAddText,
  onInsertElement,
  firebase,
  franchiseeId,
  assets,
  assetsLoading,
  assetsError,
  connected,
  needsReconnect,
  rootFolderId,
  onRefresh,
  onConnect,
  onPlaceAsset,
  onBeginAssetDrag,
  onEndAssetDrag,
}) => {
  const [layersOpen, setLayersOpen] = useState(true);
  const [uploadsLibraryOpen, setUploadsLibraryOpen] = useState(false);
  const [elementsLibraryOpen, setElementsLibraryOpen] = useState(false);
  const leftPanelRef = useRef(null);
  const libraryRef = useRef(null);
  const elementsLibraryRef = useRef(null);
  const layerCount = elements.length;

  const toolTabs = useMemo(
    () => [
      {
        id: 'templates',
        label: 'Templates',
        disabled: true,
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
            <rect x="4" y="5" width="7" height="7" rx="2" />
            <rect x="13" y="5" width="7" height="7" rx="2" />
            <rect x="4" y="14" width="7" height="7" rx="2" />
            <rect x="13" y="14" width="7" height="7" rx="2" />
          </svg>
        ),
      },
      {
        id: 'text',
        label: 'Text',
        disabled: false,
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
            <path d="M5 6h14M9 6v12M15 6v12" strokeWidth="1.5" />
          </svg>
        ),
      },
      {
        id: 'uploads',
        label: 'Uploads',
        disabled: false,
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
            <path d="M12 16V6m0 0l-4 4m4-4l4 4" strokeWidth="1.5" />
            <path d="M5 18h14" strokeWidth="1.5" />
          </svg>
        ),
      },
      {
        id: 'elements',
        label: 'Elements',
        disabled: false,
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
            <circle cx="8" cy="8" r="3" />
            <rect x="11" y="11" width="8" height="8" rx="2" />
          </svg>
        ),
      },
      {
        id: 'brand',
        label: 'Brand',
        disabled: true,
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
            <path d="M6 6h8l4 4v8H6z" strokeWidth="1.5" />
            <path d="M9 6v6h6" strokeWidth="1.5" />
          </svg>
        ),
      },
    ],
    [],
  );

  const activeLabel = toolTabs.find((tab) => tab.id === activeTool)?.label || 'Tools';

  useEffect(() => {
    if (activeTool === 'uploads') {
      setUploadsLibraryOpen(true);
    } else {
      setUploadsLibraryOpen(false);
    }
  }, [activeTool]);

  useEffect(() => {
    if (activeTool === 'elements') {
      setElementsLibraryOpen(true);
    } else {
      setElementsLibraryOpen(false);
    }
  }, [activeTool]);

  useEffect(() => {
    if (!uploadsLibraryOpen || activeTool !== 'uploads') return;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (libraryRef.current?.contains(target)) return;
      if (leftPanelRef.current?.contains(target)) return;
      setUploadsLibraryOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeTool, uploadsLibraryOpen]);

  useEffect(() => {
    if (!elementsLibraryOpen || activeTool !== 'elements') return;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (elementsLibraryRef.current?.contains(target)) return;
      if (leftPanelRef.current?.contains(target)) return;
      setElementsLibraryOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeTool, elementsLibraryOpen]);

  return (
    <div className="studio-editor-panel studio-left-panel" ref={leftPanelRef}>
      <div className="studio-left-panel-scroll">
      <div className="studio-toolbox">
        <div className="studio-toolbox-header">
          <span>Toolbox</span>
          <span className="studio-toolbox-active">{activeLabel}</span>
        </div>
        <div className="studio-toolbox-tabs">
          {toolTabs.map((tab) => {
            const isActive = activeTool === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className={`studio-toolbox-tab ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (!tab.disabled) {
                    onToolChange(tab.id);
                    if (tab.id === 'uploads') {
                      setUploadsLibraryOpen(true);
                    }
                  }
                }}
                disabled={tab.disabled}
              >
                <span className="studio-toolbox-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="studio-toolbox-content">
          {activeTool === 'text' ? (
            <div className="studio-toolbox-section">
              <div className="studio-toolbox-section-title">Add Text</div>
              <button
                type="button"
                className="studio-toolbox-action"
                onClick={() => onAddText('heading')}
              >
                Add a heading
              </button>
              <button
                type="button"
                className="studio-toolbox-action"
                onClick={() => onAddText('subheading')}
              >
                Add a subheading
              </button>
              <button
                type="button"
                className="studio-toolbox-action"
                onClick={() => onAddText('body')}
              >
                Add body text
              </button>
            </div>
          ) : activeTool === 'uploads' ? (
            <UploadsPanel
              firebase={firebase}
              assets={assets}
              loading={assetsLoading}
              error={assetsError}
              connected={connected}
              needsReconnect={needsReconnect}
              rootFolderId={rootFolderId}
              onRefresh={onRefresh}
              onConnect={onConnect}
              onPlaceAsset={onPlaceAsset}
              variant="controls"
              onUploadStart={() => setUploadsLibraryOpen(false)}
              onToggleLibrary={() => setUploadsLibraryOpen((prev) => !prev)}
              onBeginDrag={onBeginAssetDrag}
              onEndDrag={onEndAssetDrag}
            />
          ) : activeTool === 'elements' ? (
            <div className="studio-toolbox-section">
              <div className="studio-toolbox-section-title">Elements Library</div>
              <p className="studio-toolbox-helper">
                Browse shapes, graphics, and more in the Elements panel.
              </p>
              <button
                type="button"
                className="studio-toolbox-action"
                onClick={() => setElementsLibraryOpen((prev) => !prev)}
              >
                {elementsLibraryOpen ? 'Hide Elements' : 'Open Elements'}
              </button>
            </div>
          ) : (
            <div className="studio-toolbox-empty">
              <div className="studio-toolbox-section-title">{activeLabel}</div>
              <p>More tools are coming soon.</p>
            </div>
          )}
        </div>
      </div>

      <div className="studio-layer-panel">
        <button
          type="button"
          className="studio-layer-toggle"
          onClick={() => setLayersOpen((prev) => !prev)}
          aria-expanded={layersOpen}
        >
          <span>Layers</span>
          <span className="studio-layer-count">{layerCount}</span>
          <span className={`studio-layer-chevron ${layersOpen ? 'open' : ''}`}>
            v
          </span>
        </button>
        {layersOpen && (
          <>
            <div className="studio-layer-section">Page 1</div>
            <div className="studio-layer-list">
              {elements.map((element) => (
                <button
                  key={element.id}
                  type="button"
                  className={`studio-layer-item ${selectedIds.includes(element.id) ? 'active' : ''}`}
                  onClick={(event) => onSelect(element.id, event.shiftKey)}
                >
                  <span className="studio-layer-label">{getLayerLabel(element)}</span>
                  {element.locked && (
                    <span className="studio-layer-lock" aria-label="Locked">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="6" y="10" width="12" height="10" rx="2" />
                        <path d="M9 10V8a3 3 0 016 0v2" strokeWidth="1.5" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      </div>

      {activeTool === 'uploads' && uploadsLibraryOpen && (
        <div className="studio-uploads-library" ref={libraryRef}>
          <div className="studio-uploads-library-header">
            <span>Uploads Library</span>
            <button
              type="button"
              className="studio-uploads-library-close"
              onClick={() => setUploadsLibraryOpen(false)}
            >
              ×
            </button>
          </div>
          <UploadsPanel
            firebase={firebase}
            assets={assets}
            loading={assetsLoading}
            error={assetsError}
            connected={connected}
            needsReconnect={needsReconnect}
            rootFolderId={rootFolderId}
            onRefresh={onRefresh}
            onConnect={onConnect}
            onPlaceAsset={onPlaceAsset}
            variant="library"
            onBeginDrag={onBeginAssetDrag}
            onEndDrag={onEndAssetDrag}
          />
        </div>
      )}

      {activeTool === 'elements' && elementsLibraryOpen && (
        <div className="studio-uploads-library studio-elements-library" ref={elementsLibraryRef}>
          <div className="studio-uploads-library-header">
            <span>Elements Library</span>
            <button
              type="button"
              className="studio-uploads-library-close"
              onClick={() => setElementsLibraryOpen(false)}
            >
              ×
            </button>
          </div>
          <ElementsPanel
            onInsertElement={onInsertElement}
            firebase={firebase}
            franchiseeId={franchiseeId}
          />
        </div>
      )}
    </div>
  );
};

export default LayersPanel;
